/** Форматы и подписи раздела «Себестоимость» (не компоненты): деньги с копейками, скачок %, даты,
 *  источники и флаги, разбор ввода цены и GUID, кварталы, ставка склада на дату. */
import type {
  FixationKind,
  FixationStatus,
  MissingStatus,
  PreviewFlag,
  PriceSource,
  WarehouseRate,
} from "@/api/purchasePrices";
import { MINUS, NBSP, rubKop } from "../ozon-analytics/format";
import { todayIso } from "../ozon-analytics/period";

export { todayIso };

/** НДС себестоимости (решение владельца: цена с НДС = расчёт × 1,22). */
export const VAT_MULT = 1.22;

/** Деньги с копейками: «1 234,56 ₽»; null — «—». */
export function money(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : rubKop(n);
}

/** Скачок в процентах (уже ×100, как `jump_pct` бэка): «+12,3 %», «−5,0 %», «0,0 %»; null — «—». */
export function jumpText(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(p)) return "—";
  const r = Math.round(p * 10) / 10;
  const text = Math.abs(r).toFixed(1).replace(".", ",");
  const sign = r > 0 ? "+" : r < 0 ? MINUS : "";
  return `${sign}${text}${NBSP}%`;
}

/** Скачок новой цены к действующей, % с 1 знаком (как `build_preview`); нет базы — null. */
export function jumpPct(next: number | null, current: number | null | undefined): number | null {
  if (next === null || current === null || current === undefined || current === 0) return null;
  return Math.round(((next - current) / current) * 1000) / 10;
}

/** ISO-дата → «01.10.2026». */
export function dateRu(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
}

/** ISO-время → «20.10.2026 14:05» (локальное время браузера). */
export function dateTimeRu(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${p(t.getDate())}.${p(t.getMonth() + 1)}.${t.getFullYear()} ${p(t.getHours())}:${p(t.getMinutes())}`;
}

const MSK_PARTS = new Intl.DateTimeFormat("ru-RU", {
  timeZone: "Europe/Moscow",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** ISO-время → «20.10.2026 14:05 МСК» (по Москве, как расписание загрузок DWH); null — «—». */
export function dateTimeMsk(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  const p: Record<string, string> = {};
  for (const part of MSK_PARTS.formatToParts(t)) p[part.type] = part.value;
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute} МСК`;
}

export const SOURCE_STYLE: Record<PriceSource, { label: string; cls: string }> = {
  production: {
    label: "производство",
    cls: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
  },
  contract: {
    label: "контракт",
    cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
  },
  manual: {
    label: "вручную",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
};

/** Порядок флагов в счётчиках предпросмотра. */
export const FLAG_ORDER: readonly PreviewFlag[] = [
  "ok",
  "warning",
  "jump",
  "multi_spec",
  "not_computed",
  "kept_manual",
  "exists_same_date",
  "missing_in_calc",
  "issue",
];

export const FLAG_STYLE: Record<PreviewFlag, { label: string; hint: string; cls: string }> = {
  ok: {
    label: "ок",
    hint: "посчитано по методике (допущения — справочно) — пишется",
    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  },
  warning: {
    label: "предупреждение",
    hint: "цена посчитана, но может быть неверной — посмотрите замечания; пишется, если не снять галочку",
    cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  },
  jump: {
    label: "скачок цены",
    hint: "расхождение с действующей ценой больше порога — пишется, проверьте",
    cls: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-300",
  },
  multi_spec: {
    label: "выбрать рецептуру",
    hint: "посчитано по нескольким рецептурам — выберите, по какой фиксировать",
    cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
  },
  not_computed: {
    label: "не посчитано",
    hint: "цена не посчитана — не пишется; внесите вручную или дозаполните данные производства",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
  kept_manual: {
    label: "ручная/контракт",
    hint: "действует ручная или контрактная цена — фиксация её не трогает",
    cls: "bg-muted text-muted-foreground",
  },
  exists_same_date: {
    label: "цена на дату есть",
    hint: "на эту дату цена уже есть — менять через «Исправить»",
    cls: "bg-muted text-muted-foreground",
  },
  missing_in_calc: {
    label: "нет в расчёте",
    hint: "нет в расчёте производства — цена не меняется",
    cls: "bg-muted text-muted-foreground",
  },
  issue: {
    label: "проблема (старый формат)",
    hint: "фиксация сохранена до 21.10.2026: «не посчитано» и «с замечаниями» не различались",
    cls: "bg-muted text-muted-foreground",
  },
};

export const FIXATION_KIND_LABEL: Record<FixationKind, string> = {
  quarter: "квартал",
  new_items: "новинки",
};

export const FIXATION_STATUS_STYLE: Record<FixationStatus, { label: string; cls: string }> = {
  running: { label: "считается", cls: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300" },
  preview: {
    label: "предпросмотр",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
  failed: { label: "ошибка", cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300" },
  confirmed: {
    label: "подтверждена",
    cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  },
  discarded: { label: "отброшена", cls: "bg-muted text-muted-foreground" },
};

export const MISSING_STATUS_STYLE: Record<MissingStatus, { label: string; cls: string }> = {
  no_price: { label: "нет цены", cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300" },
  unmapped: {
    label: "нет сопоставления",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
  ambiguous: {
    label: "неоднозначно",
    cls: "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
  },
  no_offer: {
    label: "нет оффера",
    cls: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  },
};

/** Пояснение к строке «Без цены», которую цена здесь не исправит. */
export const MISSING_HINT: Record<Exclude<MissingStatus, "no_price">, string> = {
  unmapped: "нет сопоставления в 1С — исправить в 1С",
  ambiguous: "неоднозначное сопоставление в 1С — исправить в 1С",
  no_offer: "продажа по технической карточке маркетплейса — нет оффера",
};

export type ParsedMoney = { ok: true; value: string; num: number } | { ok: false; error: string };

/** Ввод денег: «1 234,56» / «1234.5» → строка «1234.56» для бэка (Decimal). Не больше 2 знаков
 *  после запятой; `positive` — строго > 0, иначе ≥ 0. Пусто/мусор — ошибка, не 0. */
export function parseMoney(raw: string, { positive }: { positive: boolean }): ParsedMoney {
  const s = raw.replace(/[\s\u00a0]/g, "").replace(",", ".");
  if (!s) return { ok: false, error: "введите сумму" };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) {
    return { ok: false, error: "число, не больше 2 знаков после запятой" };
  }
  const num = Number(s);
  if (positive && num <= 0) return { ok: false, error: "должно быть больше 0" };
  return { ok: true, value: s, num };
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Пустая характеристика 1С (товар без характеристик). */
export const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

/** Подпись характеристики под названием: пустая характеристика (нулевой GUID) — «без характеристики»,
 *  иначе её название; названия нет (null/отсутствует/пусто) — null, строку не рисуем. */
export function characteristicLabel(ref: string, name: string | null | undefined): string | null {
  if (ref === ZERO_GUID) return "без характеристики";
  const n = name?.trim();
  return n ? n : null;
}

/** Ссылка 1С (GUID): пробелы по краям и фигурные скобки {…} срезаются, регистр приводится к нижнему (как отдаёт OData 1С),
 *  иначе — ошибка формата. */
export function normalizeGuid(raw: string): string | null {
  let v = raw.trim().toLowerCase();
  if (v.startsWith("{") && v.endsWith("}")) v = v.slice(1, -1).trim();
  return GUID_RE.test(v) ? v : null;
}

/** Первое число квартала даты: 2026-11-15 → 2026-10-01. */
export function quarterStart(iso: string): string {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const qm = Math.floor((m - 1) / 3) * 3 + 1;
  return `${y}-${String(qm).padStart(2, "0")}-01`;
}

/** Первое число месяца даты. */
export function monthStart(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Кварталы для выбора: 4 прошлых, текущий, следующий — новые сверху. Значение — 1-е число. */
export function quarterOptions(today: string): { value: string; label: string }[] {
  const cur = quarterStart(today);
  let y = Number(cur.slice(0, 4));
  let q = (Number(cur.slice(5, 7)) - 1) / 3 + 1;
  // начинаем со следующего
  q += 1;
  if (q > 4) {
    q = 1;
    y += 1;
  }
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < 6; i += 1) {
    const value = `${y}-${String((q - 1) * 3 + 1).padStart(2, "0")}-01`;
    out.push({ value, label: `${["I", "II", "III", "IV"][q - 1]} кв. ${y} (с ${dateRu(value)})` });
    q -= 1;
    if (q < 1) {
      q = 4;
      y -= 1;
    }
  }
  return out;
}

/** Ставка склада на дату: строка с max `valid_from ≤ date` (как бэкенд); нет — null. */
export function rateOn(rates: WarehouseRate[], date: string): WarehouseRate | null {
  let best: WarehouseRate | null = null;
  for (const r of rates) {
    if (r.valid_from <= date && (best === null || r.valid_from > best.valid_from)) best = r;
  }
  return best;
}

/** Характеристика товара 1С в окне цены — всегда словами: нулевой GUID — «без характеристики»,
 *  имя есть — имя, имени нет — «имя в 1С не указано» (характеристика есть, но без подписи). */
export function characteristic1c(ref: string, name: string | null | undefined): string {
  if (ref === ZERO_GUID) return "без характеристики";
  const n = name?.trim();
  return n ? n : "имя в 1С не указано";
}

/** Себестоимость = себестоимость производства с НДС (`price_gross`, хранимая) + ставка склада на дату (₽/шт,
 *  как есть). Нет ставки — null (показывать «ставка склада на дату не задана», а не цену без склада). */
export function purchasePrice(gross: number, rate: WarehouseRate | null): number | null {
  return rate === null ? null : Math.round((gross + rate.rub_per_unit) * 100) / 100;
}
